// hooks/useBenchmarkDB.js — Benchmark Pricing Database state and CRUD
import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../services/msalService";
import {
  fetchBenchmarks,
  createBenchmark,
  updateBenchmark,
  deleteBenchmark,
} from "../services/benchmarkService";

const STORAGE_KEY = "lekoil_benchmark_db";

function loadLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return [];
}

function saveLocal(records) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(records)); } catch {}
}

export function useBenchmarkDB() {
  const { instance, accounts } = useMsal();
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [spError, setSpError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setSpError(null);
    try {
      const token = await getAccessToken(instance, accounts);
      const list  = await fetchBenchmarks(token);
      setRecords(list);
      setIsDemoMode(false);
    } catch (err) {
      console.error("[BenchmarkDB] SharePoint connection failed:", err.message);
      setSpError(err.message);
      setRecords(loadLocal());
      setIsDemoMode(true);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { load(); }, [load]);

  const addRecord = useCallback(async (data) => {
    const now  = new Date().toISOString();
    const item = { ...data, id: `bm-${Date.now()}`, createdAt: now, updatedAt: now };
    if (isDemoMode) {
      const updated = [item, ...records];
      setRecords(updated);
      saveLocal(updated);
      return item;
    }
    const token   = await getAccessToken(instance, accounts);
    const created = await createBenchmark(token, item);
    setRecords((prev) => [created, ...prev]);
    return created;
  }, [isDemoMode, records, instance, accounts]);

  // ── Bulk create (Excel upload) ──────────────────────────────────────────────

  const addRecords = useCallback(async (list, { submittedBy, submittedByName, onProgress } = {}) => {
    const now = new Date().toISOString();
    const items = list.map((data, i) => ({
      ...data,
      id: `bm-${Date.now()}-${i}`,
      submittedBy: data.submittedBy || submittedBy || "",
      submittedByName: data.submittedByName || submittedByName || "",
      createdAt: now,
      updatedAt: now,
    }));

    if (isDemoMode) {
      const updated = [...[...items].reverse(), ...records];
      setRecords(updated);
      saveLocal(updated);
      return { created: items, failed: [] };
    }

    const token = await getAccessToken(instance, accounts);
    const created = [];
    const failed = [];
    for (let i = 0; i < items.length; i++) {
      try {
        created.push(await createBenchmark(token, items[i]));
      } catch (err) {
        failed.push({ item: items[i], error: err.message });
      }
      onProgress?.(i + 1, items.length);
    }
    if (created.length > 0) {
      setRecords((prev) => [...[...created].reverse(), ...prev]);
    }
    return { created, failed };
  }, [isDemoMode, records, instance, accounts]);

  const editRecord = useCallback(async (id, data) => {
    const target  = records.find((r) => r.id === id);
    if (!target) return null;
    const updated = { ...target, ...data, updatedAt: new Date().toISOString() };
    if (isDemoMode) {
      const list = records.map((r) => (r.id === id ? updated : r));
      setRecords(list);
      saveLocal(list);
      return updated;
    }
    const token = await getAccessToken(instance, accounts);
    await updateBenchmark(token, target._spItemId, updated);
    setRecords((prev) => prev.map((r) => (r.id === id ? updated : r)));
    return updated;
  }, [isDemoMode, records, instance, accounts]);

  const removeRecord = useCallback(async (id) => {
    if (isDemoMode) {
      const list = records.filter((r) => r.id !== id);
      setRecords(list);
      saveLocal(list);
      return;
    }
    const target = records.find((r) => r.id === id);
    if (!target?._spItemId) throw new Error("No SharePoint ID");
    const token = await getAccessToken(instance, accounts);
    await deleteBenchmark(token, target._spItemId);
    setRecords((prev) => prev.filter((r) => r.id !== id));
  }, [isDemoMode, records, instance, accounts]);

  return {
    records,
    loading,
    isDemoMode,
    spError,
    refresh: load,
    addRecord,
    addRecords,
    editRecord,
    removeRecord,
  };
}
